import React, { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Button, Input, Radio, Flex } from "antd";
import { Hint } from "@/common/hint";
import CustomSelect from "@/common/custom-select";
import PageSizeSelector from '@/common/page-size-selector';
import { clearFilter } from "@/pages/shared/shared.reducer";

export const SubTabControls = ({
  setLargeTableView,
  setThumbnailTableView,
  isLargeView,
  isThumbnailView,
  isCartTab,
  inDrawer = false,
  width = window.innerWidth,
  allSites = false,
  totalCount = 0,
  siteTotalCount = 0,
  handleFilterDrawerVisible,
  tableId
}) => {
  const selectedView = isLargeView ? "left" : isThumbnailView ? "right" : undefined;
  return (
    <div
      style={{
        display: !inDrawer ? "flex" : "",
        justifyContent: width < 768 ? "end" : "space-between",
        gap: "15px",
      }}
    >
      {!inDrawer && (
        <Flex gap={"20px"} align="center" justify="end">
          <div
            key={1}
            className={"sub-tabs-dashboard-button dark"}
          >
          <span className="mobile-table-tab">
            {totalCount}
          </span>
          Results
          </div>
          {siteTotalCount > 0 && !allSites && (
            <div
              key={2}
              className={"sub-tabs-dashboard-button light"}
            >
              <span className="mobile-table-tab">
                {siteTotalCount}
              </span>
              Total
            </div>
          )}
        </Flex>
      )}
        <div
          className="top-dashboard-panel-view"
          style={{ display: !inDrawer ? "flex" : "grid"}}
        >
        {!allSites && (
          <Radio.Group
            block={inDrawer}
            style={{ display: "flex" }}
            value={selectedView}
          >
            <Radio.Button
              value="left"
              aria-label="Multiline view"
              onClick={() => setLargeTableView(isCartTab)}
              style={{ flex: 1, textAlign: "center" }}
            >
              <Hint
                placement="top"
                width="200px"
                popoverText="Multiline Descriptions - Descriptions have a line for each description"
              >
                <i className="ri-menu-line" />
              </Hint>
            </Radio.Button>
            <Radio.Button
              value="right"
              aria-label="Thumbnail view"
              onClick={() => setThumbnailTableView(isCartTab)}
              style={{ flex: 1, textAlign: "center" }}
            >
              <Hint
                placement="top"
                width="200px"
                popoverText="Thumbnail view - Small images shown of the document/drawing."
              >
                <i className="ri-function-line" />
              </Hint>
            </Radio.Button>
          </Radio.Group>
        )}
        {inDrawer && (
          <div className="display-center mt-2"
            style={{ gap: "5px" }}>
            <PageSizeSelector
              tableId={tableId}
              tableDataCount={totalCount}
            />
            <span> of {totalCount}</span>
          </div>
        )}
        {!inDrawer && width < 1024 && (
          <Button
            className="small-filter display-center ms-2"
            onClick={() => handleFilterDrawerVisible && handleFilterDrawerVisible(true)}
            style={width < 600 ? { width: "100%" } : {}}
          >
            <i className="ri-filter-3-line"></i>
            Filters
          </Button>
        )}
        </div>
    </div>
  );
};

const SubTabButtons = ({
  activeTabKey,
  handleSubTabsChange,
  ddvSearch = "",
  setDdvSearch,
  altSearch = "",
  descriptionSearch = "",
  setAltSearch,
  setDescriptionSearch,
  handleSearch,
  subTabsOptions,
  width,
  handleFilterDrawerVisible,
  setLargeTableView,
  setThumbnailTableView,
  mobileView,
  searchText,
  altSearchText,
  swapDdvAndAltSearch = false,
  ddvSearchInputWidth = "280px",
  altSearchInputWidth = "280px",
  descriptionSearchInputWidth = "280px",
  searchLayoutClassName = "",
  isLargeView,
  isThumbnailView,
  isCartTab,
  allSites = false,
  searchBarRef,
  totalCount = 0,
  siteTotalCount = 0,
  tableId
}) => {
  const dispatch = useDispatch();
  
  const localDDVSearch = ddvSearch || "";
  const setLocalDDVSearch = useMemo(() => setDdvSearch || (() => {}), [setDdvSearch]);
  const localDescriptionSearch = descriptionSearch || "";
  const setLocalDescriptionSearch = useMemo(() => setDescriptionSearch || (() => {}), [setDescriptionSearch]);
  const localAltSearch = altSearch || "";
  const setLocalAltSearch = useMemo(() => setAltSearch || (() => {}), [setAltSearch]);

  const resetSearch = useCallback(() => {
    setLocalDDVSearch("");
    setLocalAltSearch("");
    setLocalDescriptionSearch("");
    handleSearch("", "", "");
    dispatch(clearFilter(tableId, null))
  }, [setLocalDDVSearch, setLocalAltSearch, setLocalDescriptionSearch, handleSearch, dispatch, tableId]);

  return (
    <div className="top-dashboard-panel">
      {mobileView ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            textAlign: "center",
            width: "100%",
            rowGap: "10px",
          }}
        >
          <CustomSelect
            defaultValue={subTabsOptions.find((x) => x.key == "1")?.key}
            value={activeTabKey}
            className="table-tab-mobile"
            placeholder="Select a tab"
            onChange={handleSubTabsChange}
            optionFilterProp="label"
            style={{ width: "100%" }}
            originalOptions={subTabsOptions}
            options={subTabsOptions.map((option) => ({
              value: option.key,
              label: option.label,
              icon: option.icon,
              count: option.count
            }))}
          />
          <Button
            className="small-filter display-center"
            onClick={() => handleFilterDrawerVisible(true)}
          >
            <i className="ri-filter-3-line"></i>
            Filters
          </Button>
        </div>
      ) : (
        <div style={{ width: "100%", display: "grid" }}>
          <div
            style={{
              display: width > 1440 || setAltSearch ? "flex" : "block",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
          {width > 1440 && (
            <Flex>
              <div ref={searchBarRef}
                className={`top-dashboard-panel-search ${searchLayoutClassName}`.trim()}
                style={{
                  display: "flex",
                  gap: "15px",
                  alignItems: "center",
                  flexDirection: "row",
                }}
              >
                {setAltSearch && swapDdvAndAltSearch && (
                  <Input
                    rootClassName="description"
                    prefix={
                      <i
                        className="ri-search-line"
                        style={{ cursor: "auto" }}
                      />
                    }
                    suffix={
                      localAltSearch && (
                        <i
                          className="ri-close-line"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setLocalAltSearch("");
                            handleSearch(localDDVSearch, localDescriptionSearch, "");
                          }}
                        />
                      )
                    }
                    placeholder={altSearchText || "Search"}
                    style={{ fontSize: "12px", width: altSearchInputWidth }}
                    value={localAltSearch}
                    onChange={(e) => setLocalAltSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter")
                        handleSearch(localDDVSearch, localDescriptionSearch, localAltSearch);
                    }}
                  />
                )}
                <Input
                  rootClassName="documents document-search"
                  prefix={
                    <i className="ri-search-line" style={{ cursor: "auto" }} />
                  }
                  suffix={
                    localDDVSearch && (
                      <i
                        className="ri-close-line"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setLocalDDVSearch("");
                          handleSearch("", localDescriptionSearch, localAltSearch);
                        }}
                      />
                    )
                  }
                  placeholder={
                    searchText || "Search document/drawing # or vendor #"
                  }
                  style={{
                    fontSize: "12px",
                    width: ddvSearchInputWidth,
                  }}
                  value={localDDVSearch}
                  onChange={(e) => setLocalDDVSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter")
                      handleSearch(localDDVSearch, localDescriptionSearch, localAltSearch);
                  }}
                />
                {setAltSearch && !swapDdvAndAltSearch && (
                  <Input
                    rootClassName="description"
                    prefix={
                      <i
                        className="ri-search-line"
                        style={{ cursor: "auto" }}
                      />
                    }
                    suffix={
                      localAltSearch && (
                        <i
                          className="ri-close-line"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setLocalAltSearch("");
                            handleSearch(localDDVSearch, localDescriptionSearch, "");
                          }}
                        />
                      )
                    }
                    placeholder={altSearchText || "Search"}
                    style={{ fontSize: "12px", width: altSearchInputWidth }}
                    value={localAltSearch}
                    onChange={(e) => setLocalAltSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter")
                        handleSearch(localDDVSearch, localDescriptionSearch, localAltSearch);
                    }}
                  />
                )}
                {setDescriptionSearch && (
                  <Input
                    rootClassName="description description-search"
                    prefix={
                      <i
                        className="ri-search-line"
                        style={{ cursor: "auto" }}
                      />
                    }
                    suffix={
                      localDescriptionSearch && (
                        <i
                          className="ri-close-line"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setLocalDescriptionSearch("");
                            handleSearch(localDDVSearch, "", localAltSearch);
                          }}
                        />
                      )
                    }
                    placeholder="Search description"
                    style={{
                      fontSize: "12px",
                      width: descriptionSearchInputWidth,
                    }}
                    value={localDescriptionSearch}
                    onChange={(e) => setLocalDescriptionSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter")
                        handleSearch(localDDVSearch, localDescriptionSearch, localAltSearch);
                    }}
                  />
                )}
              <Flex className="dashboard-search-actions">
                <div style={{ margin: "5px" }}>
                  <Button
                    type="primary"
                    className="upload-document-button display-center w-100"
                    onClick={() =>
                      handleSearch(
                        localDDVSearch,
                        localDescriptionSearch,
                        localAltSearch
                      )
                    }
                  >
                    <div
                      style={{
                        display: "flex",
                        marginLeft: "auto",
                        marginRight: "auto",
                        gap: "6px",
                      }}
                      className="display-center"
                    >
                      <i
                        style={{ fontSize: 16 }}
                        className="ri-checkbox-circle-line"
                      ></i>
                      <div>Apply</div>
                    </div>
                  </Button>
                </div>
                <div style={{ margin: "5px" }}>
                  <Button
                    type="default"
                    className="display-center reset-filter-button w-100"
                    onClick={() => {
                      resetSearch();
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        marginLeft: "auto",
                        marginRight: "auto",
                        gap: "6px",
                      }}
                      className="display-center"
                    >
                      <div>Reset</div>
                    </div>
                  </Button>
                </div>
              </Flex>
              </div>
            </Flex>
          )}
            {setAltSearch && width <= 1440 && !searchLayoutClassName && (
              <Input
                rootClassName="description"
                prefix={
                  <i
                    className="ri-search-line"
                    style={{ cursor: "auto" }}
                  />
                }
                suffix={
                  localAltSearch && (
                    <i
                      className="ri-close-line"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setLocalAltSearch("");
                        handleSearch(localDDVSearch, localDescriptionSearch, "");
                      }}
                    />
                  )
                }
                placeholder={altSearchText || ""}
                style={{ fontSize: "12px", width: altSearchInputWidth }}
                value={localAltSearch}
                onChange={(e) => setLocalAltSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter")
                    handleSearch(localDDVSearch, localDescriptionSearch, localAltSearch);
                }}
              />
            )}
            {width >= 768 && (
              <SubTabControls
                isThumbnailView={isThumbnailView}
                isLargeView={isLargeView}
                setLargeTableView={setLargeTableView}
                setThumbnailTableView={setThumbnailTableView}
                isCartTab={isCartTab}
                mobileView={mobileView}
                width={width}
                allSites={allSites}
                totalCount={totalCount}
                siteTotalCount={siteTotalCount}
                handleFilterDrawerVisible={() => handleFilterDrawerVisible(true)}
                tableId={tableId}
              />
            )}
          </div>
          {width <= 1440 && (
            <div
              style={{ marginTop: "10px", justifyContent: "space-between" }}
              className="top-dashboard-panel-search"
            >
              {width >= 768 && (
                <div ref={searchBarRef}
                  className={searchLayoutClassName}
                  style={{ 
                    display: "flex", 
                    gap: "15px",
                    marginBottom: "5px"}}
                >
                  {setAltSearch && swapDdvAndAltSearch && searchLayoutClassName && (
                    <Input
                      rootClassName="description"
                      prefix={
                        <i
                          className="ri-search-line"
                          style={{ cursor: "auto" }}
                        />
                      }
                      suffix={
                        localAltSearch && (
                          <i
                            className="ri-close-line"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              setLocalAltSearch("");
                              handleSearch(localDDVSearch, localDescriptionSearch, "");
                            }}
                          />
                        )
                      }
                      placeholder={altSearchText || ""}
                      style={{ fontSize: "12px", width: altSearchInputWidth }}
                      value={localAltSearch}
                      onChange={(e) => setLocalAltSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter")
                          handleSearch(localDDVSearch, localDescriptionSearch, localAltSearch);
                      }}
                    />
                  )}
                  <Input
                    rootClassName="documents document-search"
                    prefix={
                      <i
                        className="ri-search-line"
                        style={{ cursor: "auto" }}
                      />
                    }
                    suffix={
                      localDDVSearch && (
                        <i
                          className="ri-close-line"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setLocalDDVSearch("");
                            handleSearch("", localDescriptionSearch, localAltSearch);
                          }}
                        />
                      )
                    }
                    placeholder={searchText || "Search doc/dwg/vendor #"}
                    style={{
                      fontSize: "12px",
                      width: searchLayoutClassName
                        ? ddvSearchInputWidth
                        : `${width < 1024 ? "200px" : "280px"}`,
                    }}
                    value={localDDVSearch}
                    onChange={(e) => setLocalDDVSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter")
                        handleSearch(localDDVSearch, localDescriptionSearch, localAltSearch);
                    }}
                  />
                  {setAltSearch && width > 1440 && (
                    <Input
                      rootClassName="description"
                      prefix={
                        <i
                          className="ri-search-line"
                          style={{ cursor: "auto" }}
                        />
                      }
                      suffix={
                        localAltSearch && (
                          <i
                            className="ri-close-line"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              setLocalAltSearch("");
                              handleSearch(localDDVSearch, localDescriptionSearch, "");
                            }}
                          />
                        )
                      }
                      placeholder={altSearchText || ""}
                      style={{ fontSize: "12px", width: "280px" }}
                      value={localAltSearch}
                      onChange={(e) => setLocalAltSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter")
                          handleSearch(localDDVSearch, localDescriptionSearch, localAltSearch);
                      }}
                    />
                )}
                  {setDescriptionSearch && (
                    <Input
                      rootClassName="description description-search"
                      prefix={
                        <i
                          className="ri-search-line"
                          style={{ cursor: "auto" }}
                        />
                      }
                      suffix={
                        localDescriptionSearch && (
                          <i
                            className="ri-close-line"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              setLocalDescriptionSearch("");
                              handleSearch(localDDVSearch, "", localAltSearch);
                            }}
                          />
                        )
                      }
                      placeholder="Search description"
                      style={{
                        fontSize: "12px",
                        width: searchLayoutClassName
                          ? descriptionSearchInputWidth
                          : `${width < 1024 ? "200px" : "280px"}`,
                      }}
                      value={localDescriptionSearch}
                      onChange={(e) => setLocalDescriptionSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter")
                          handleSearch(localDDVSearch, localDescriptionSearch, localAltSearch);
                      }}
                    />
                  )}
                  <Flex className="dashboard-search-actions">
                    <div>
                      <Button
                        className="upload-document-button display-center w-100"
                        onClick={() =>
                          handleSearch(
                            localDDVSearch,
                            localDescriptionSearch
                          )
                        }
                      >
                        <div
                          style={{
                            display: "flex",
                            marginLeft: "auto",
                            marginRight: "auto",
                            gap: "6px",
                          }}
                          className="display-center"
                        >
                          <i
                            style={{ fontSize: 16 }}
                            className="ri-checkbox-circle-line"
                          ></i>
                          <div>Apply</div>
                        </div>
                      </Button>
                    </div>
                    <div style={{ paddingLeft: "8px" }}>
                      <Button
                        className="display-center reset-filter-button w-100"
                        onClick={() => {
                          resetSearch();
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            marginLeft: "auto",
                            marginRight: "auto",
                            gap: "6px",
                          }}
                          className="display-center"
                        >
                          <div>Reset</div>
                        </div>
                      </Button>
                    </div>
                  </Flex>
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  gap: "15px",
                  justifySelf: "end",
                }}
              >
                {width >= 1024 && (
                  <Button
                    className="small-filter display-center"
                    onClick={() => handleFilterDrawerVisible(true)}
                    style={width < 600 ? { width: "100%" } : {}}
                  >
                    <i className="ri-filter-3-line"></i>
                    Filters
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SubTabButtons;
